<template>
  <!-- Compact timestamp ("25 min", "3 w") that reveals the full stamp
       ("2026-04-22 - 15:03") on hover, or on tap on touch devices — the long
       "about 3 weeks ago" phrasing ate a whole header row next to the username.

       The two activators are mutually exclusive on purpose. `open-on-click` does
       not disable `open-on-hover` (VTooltip defaults it to true), and the click
       activator is a toggle. Vuetify only guards the hover activator against
       touch-emulated mouse events via `sourceCapabilities`, which is a
       Chromium-only API — on iOS/WKWebView a single tap therefore fires an
       emulated `mouseenter` that opens the tooltip and an emulated `click` that
       immediately toggles it shut again, so the date only stuck on the second
       tap. Do not re-add `open-on-hover` unconditionally.

       VTooltip hardcodes `persistent: true` on its overlay, so there is no
       click-outside close; with hover off on touch, Vuetify's own `mouseleave`
       close path is gone too, hence the explicit v-model + @mouseleave. -->
  <v-tooltip
    v-model="open"
    :text="absolute"
    location="top"
    :open-on-hover="hasHover"
    :open-on-click="!hasHover"
  >
    <template #activator="{ props: activatorProps }">
      <span
        v-bind="activatorProps"
        class="relative-time text-neutral400 text-body-2"
        v-text="label"
        @mouseleave="closeOnTouchLeave"
      />
    </template>
  </v-tooltip>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHasHover } from '@/composables/useHasHover'
import { getCompactDistance, compactDistanceKey, formatDateTimeAbsolute } from '@/utils/date-format'

const props = defineProps({
  // ISO string or Date. Null renders nothing (item not yet persisted).
  date: {
    type: [String, Date],
    default: null,
  },
})

const { t, locale } = useI18n()
const { hasHover } = useHasHover()

const open = ref(false)

// Touch only. With the hover activator off there is no Vuetify `mouseleave`
// close left, and the overlay is persistent, so tapping elsewhere would strand
// the tooltip open. On pointer devices Vuetify already handles this and honours
// `close-delay`, so don't shadow it.
const closeOnTouchLeave = () => {
  if (!hasHover.value) open.value = false
}

const parsedDate = computed(() => (props.date ? new Date(props.date) : null))

const label = computed(() => {
  if (!parsedDate.value) return ''
  const { unit, count } = getCompactDistance(parsedDate.value)
  return t(compactDistanceKey(unit), { n: count })
})

const absolute = computed(() => {
  if (!parsedDate.value) return ''
  return formatDateTimeAbsolute(parsedDate.value, locale.value)
})
</script>

<style lang="scss" scoped>
// Signals the tooltip affordance without turning the timestamp into a link.
.relative-time {
  cursor: default;
  white-space: nowrap;
}
</style>
